import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { FaGlobe, FaRegEdit } from "react-icons/fa"
import { useLocation, useNavigate } from "react-router-dom"
import { routesMap } from "../routesMap"

const languages = [
  { code: "en", lang: "English" },
  { code: "it", lang: "Italian" },
]

const getLocalizedPath = (path, language) => {
  console.log("Getting localized path for:", { path, language })

  // First, normalize the path by removing any language prefix
  const normalizedPath = path.replace(/^\/(en|it)/, "") || "/"
  console.log("Normalized path:", normalizedPath)

  // Create reverse mapping from all paths to their base routes
  const reverseMap = {}
  Object.entries(routesMap).forEach(([baseRoute, translations]) => {
    // Map both Italian and English paths to the base route
    reverseMap[translations.it] = baseRoute
    reverseMap[translations.en.replace("/en", "")] = baseRoute
    reverseMap[baseRoute] = baseRoute
  })
  console.log("Reverse map:", reverseMap)

  // Find the base route for the current path
  const baseRoute = reverseMap[normalizedPath]
  console.log("Base route found:", baseRoute)

  if (!baseRoute) {
    console.warn("No base route found for path:", normalizedPath)
    return language === "en" ? `/en${normalizedPath}` : normalizedPath
  }

  // Get the localized version
  const localizedPath = routesMap[baseRoute][language]
  console.log("Localized path:", localizedPath)

  return localizedPath
}

const LanguageSelector = () => {
  const { i18n } = useTranslation()
  const [hovered, setHovered] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Change this constant to set your desired password
  const EDIT_PASSWORD = "justtomakesure"
  const STORAGE_KEY = "translatorAuth"
  const EXPIRATION_DAYS = 30

  const checkAuthStatus = () => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return false

    try {
      const { expiry } = JSON.parse(stored)
      if (new Date().getTime() > expiry) {
        localStorage.removeItem(STORAGE_KEY)
        return false
      }
      return true
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY)
      return false
    }
  }

  const setAuthStatus = () => {
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + EXPIRATION_DAYS)
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        authenticated: true,
        expiry: expiryDate.getTime(),
      })
    )
  }

  const toggleEditMode = () => {
    if (window.location.hash === "#translate") {
      window.history.replaceState(null, null, " ")
    } else {
      const isAuthenticated = checkAuthStatus()

      if (isAuthenticated) {
        window.location.hash = "#translate"
      } else {
        const password = prompt("Enter password to enable translation editor:")
        if (password === EDIT_PASSWORD) {
          setAuthStatus()
          window.location.hash = "#translate"
        } else {
          alert("Incorrect password. Access denied.")
        }
      }
    }
  }

  // Effect to handle initial language based on URL
  useEffect(() => {
    const path = location.pathname
    const shouldBeEnglish = path.startsWith("/en")
    if (shouldBeEnglish && i18n.language !== "en") {
      i18n.changeLanguage("en")
    } else if (!shouldBeEnglish && i18n.language !== "it") {
      i18n.changeLanguage("it")
    }
  }, [location.pathname, i18n])

  const changeLanguage = async (lng) => {
    if (lng === i18n.language) {
      setHovered(false)
      return
    }

    // Get new path before changing language
    const currentPath = location.pathname
    const newPath = getLocalizedPath(currentPath, lng)

    // Change language and load translations
    await i18n.changeLanguage(lng)

    // Update URL and navigate
    navigate(newPath, { replace: true })
    setHovered(false)
  }

  const handleMouseEnter = () => setHovered(true)
  const handleMouseLeave = () => setHovered(false)

  return (
    <div className="relative z-40 flex">
      {/*  <button
        onClick={toggleEditMode}
        className="ml-4 px-2 py-1 mr-2 bg-secondary-400 rounded"
      >
        {window.location.hash === "#translate" ? "X" : <FaRegEdit />}
      </button> */}
      <button
        className="focus:outline-none text-background-dark bg-white/50 rounded-full px-3 flex items-center gap-0"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <FaGlobe className="mr-2" />
        {languages.find((lng) => lng.code === i18n.language)?.lang}
      </button>
      <div
        className={`absolute top-full bg-white/70 rounded-full right-0 shadow z-40 transition-transform-opacity duration-500 ease-custom-easing transform ${
          hovered
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {languages
          .filter((lng) => lng.code !== i18n.language)
          .map((lng) => (
            <div
              key={lng.code}
              className="w-full text-left text-secondary-200 hover:text-white px-3 rounded-full hover:bg-secondary-200 py-1 cursor-pointer"
              onClick={() => changeLanguage(lng.code)}
            >
              {lng.lang}
            </div>
          ))}
      </div>
    </div>
  )
}

export default LanguageSelector
